/**
 * Gate Service - Business logic for gate entry/exit operations
 */

import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_STATUS } from '../../constants/statuses';
import { VEHICLE_MOVEMENT_EVENTS } from '../../constants/systemConfig';
import { updateReservation, createReservation, getReservationsByDateRange } from './reservationService';
import {
    logFacilityEntry,
    logFacilityExit,
    logVehicleMovement,
    getVehicleMovementLogByReservation,
    createVehicleMovementLog
} from '../logging/vehicleMovementLogger';
import { getTransporterDrivers, createDriver, updateDriver } from '../firestore/driverService';

/**
 * Search vehicles by plate number and/or transporter
 */
export const searchVehicles = async (tenantId, filters = {}) => {
    const { plate, transporterId, startDate, endDate } = filters;

    try {
        // Default to today if no date range provided
        const start = startDate || new Date().toISOString().split('T')[0];
        const end = endDate || new Date().toISOString().split('T')[0];

        let reservations = await getReservationsByDateRange(tenantId, start, end);

        // Apply filters
        if (plate) {
            const upperPlate = plate.toUpperCase();
            reservations = reservations.filter(r =>
                (r.plateNumber || '').toUpperCase().includes(upperPlate) ||
                (r.reservationNumber || '').toUpperCase().includes(upperPlate)
            );
        }

        if (transporterId) {
            reservations = reservations.filter(r =>
                r.transporterId === transporterId || r.transporterName === transporterId
            );
        }

        return reservations;
    } catch (error) {
        throw error;
    }
};

/**
 * Sync driver info with transporter's driver list
 */
export const syncDriverInfo = async (driverData, tenantId) => {
    const { transporterId, driverName, driverPhone, driverIdentityId } = driverData;

    if (!transporterId || !driverIdentityId || !tenantId) return;

    try {
        const drivers = await getTransporterDrivers(transporterId, tenantId);
        const existingDriver = drivers.find(d => (d.identityId || d.tcNo) === driverIdentityId);

        if (existingDriver) {
            // Update if name or phone changed
            if (existingDriver.name !== driverName || existingDriver.phone !== driverPhone) {
                await updateDriver(existingDriver.id, {
                    name: driverName,
                    phone: driverPhone
                }, tenantId);
            }
        } else {
            // Create new driver record
            await createDriver({
                name: driverName,
                phone: driverPhone,
                identityId: driverIdentityId,
                transporterId,
                isActive: true
            }, tenantId);
        }
    } catch (error) {
        console.error('Driver sync failed', error);
        // We don't throw here to avoid blocking the main gate operation
    }
};

/**
 * Process vehicle entry
 */
export const processVehicleEntry = async (reservationId, plateNumber, tenantId, performedBy, notes = '', updatedDetails = {}) => {
    try {
        // Log facility entry
        await logFacilityEntry(reservationId, plateNumber, tenantId, performedBy);

        // Update reservation status and any edited details
        await updateReservation(reservationId, {
            status: VEHICLE_STATUS.FACILITY_ENTERED,
            entryTime: new Date(),
            entryNotes: notes,
            entryPerformedBy: performedBy || null,
            ...updatedDetails // Persist edited fields like plateNumber, driverName, driverPhone
        }, performedBy, tenantId);

        // Sync driver info
        if (updatedDetails.driverIdentityId) {
            await syncDriverInfo({
                transporterId: updatedDetails.transporterId || null,
                driverName: updatedDetails.driverName,
                driverPhone: updatedDetails.driverPhone,
                driverIdentityId: updatedDetails.driverIdentityId
            }, tenantId);
        }

        // Add note to movement log if provided
        if (notes) {
            const log = await getVehicleMovementLogByReservation(reservationId, tenantId);
            if (log) {
                await logVehicleMovement(log.id, {
                    eventType: 'NOTE_ADDED',
                    performedBy,
                    metadata: { note: notes, context: 'entry' }
                }, tenantId);
            }
        }

        return { success: true };
    } catch (error) {
        throw error;
    }
};

/**
 * Process vehicle exit
 */
export const processVehicleExit = async (reservationId, performedBy, tenantId, notes = '', updatedDetails = {}) => {
    try {
        // Get movement log
        const log = await getVehicleMovementLogByReservation(reservationId, tenantId);

        if (log) {
            // Log facility exit
            await logFacilityExit(log.id, performedBy, tenantId);

            // Add note if provided
            if (notes) {
                await logVehicleMovement(log.id, {
                    eventType: 'NOTE_ADDED',
                    performedBy,
                    metadata: { note: notes, context: 'exit' }
                }, tenantId);
            }
        }

        // Update reservation status and any edited details
        await updateReservation(reservationId, {
            status: VEHICLE_STATUS.FACILITY_EXITED,
            exitTime: new Date(),
            exitNotes: notes,
            exitPerformedBy: performedBy || null,
            ...updatedDetails
        }, performedBy, tenantId);

        return { success: true };
    } catch (error) {
        throw error;
    }
};

/**
 * Send vehicle to parking area
 */
export const sendToParking = async (reservationId, plateNumber, tenantId, performedBy, notes = '', updatedDetails = {}) => {
    try {
        // Get or create movement log
        let log = await getVehicleMovementLogByReservation(reservationId, tenantId);

        if (!log) {
            const logId = await createVehicleMovementLog(reservationId, plateNumber, tenantId);
            log = { id: logId };
        }

        // Log parking event
        await logVehicleMovement(log.id, {
            eventType: VEHICLE_MOVEMENT_EVENTS.SENT_TO_PARKING,
            location: 'Park Alanı',
            performedBy,
            metadata: { note: notes }
        }, tenantId);

        // Update reservation status and edited details
        await updateReservation(reservationId, {
            status: VEHICLE_STATUS.IN_PARKING,
            parkingTime: new Date(),
            parkingNotes: notes,
            parkingPerformedBy: performedBy || null,
            ...updatedDetails
        }, performedBy, tenantId);

        return { success: true };
    } catch (error) {
        throw error;
    }
};

/**
 * Create instant (walk-in) reservation
 */
export const createInstantReservation = async (vehicleData, performedBy) => {
    try {
        const now = new Date();
        const endTime = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours from now

        const reservationData = {
            tenantId: vehicleData.tenantId,
            plateNumber: vehicleData.plateNumber.toUpperCase(),
            transporterId: vehicleData.transporterId || null,
            transporterName: vehicleData.transporterName || 'Bilinmiyor',
            vehicleTypeId: vehicleData.vehicleTypeId || null,
            vehicleType: vehicleData.vehicleType || null,
            driverName: vehicleData.driverName || null,
            driverPhone: vehicleData.driverPhone || null,
            status: VEHICLE_STATUS.FACILITY_ENTERED,
            startTime: now,
            endTime: endTime,
            bookingDate: now.toISOString().split('T')[0],
            isInstant: true,
            notes: vehicleData.notes || '',
            createdBy: performedBy,
            entryTime: now,
            entryPerformedBy: performedBy || null,
            driverIdentityId: vehicleData.driverIdentityId || null
        };

        const reservationId = await createReservation(reservationData, performedBy);

        // Sync driver info
        if (vehicleData.driverIdentityId && vehicleData.transporterId) {
            await syncDriverInfo({
                transporterId: vehicleData.transporterId,
                driverName: vehicleData.driverName,
                driverPhone: vehicleData.driverPhone,
                driverIdentityId: vehicleData.driverIdentityId
            }, vehicleData.tenantId);
        }

        // Log facility entry
        await logFacilityEntry(reservationId, reservationData.plateNumber, vehicleData.tenantId, performedBy);

        return { success: true, reservationId };
    } catch (error) {
        throw error;
    }
};

/**
 * Get today's gate statistics
 */
export const getTodayStatistics = async (tenantId) => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const reservations = await getReservationsByDateRange(tenantId, today, today);

        const stats = {
            total: reservations.length,
            entered: 0,
            exited: 0,
            inParking: 0,
            loading: 0,
            pending: 0
        };

        reservations.forEach(r => {
            switch (r.status) {
                case VEHICLE_STATUS.FACILITY_ENTERED:
                case VEHICLE_STATUS.LOADING_STARTED:
                case VEHICLE_STATUS.LOADING_COMPLETED:
                    stats.entered++;
                    if ([VEHICLE_STATUS.LOADING_STARTED, VEHICLE_STATUS.LOADING_COMPLETED].includes(r.status)) {
                        stats.loading++;
                    }
                    break;
                case VEHICLE_STATUS.FACILITY_EXITED:
                    stats.exited++;
                    break;
                case VEHICLE_STATUS.IN_PARKING:
                    stats.inParking++;
                    break;
                default:
                    if (r.status === VEHICLE_STATUS.PENDING_APPROVAL || r.status === VEHICLE_STATUS.APPROVED) {
                        stats.pending++;
                    }
            }
        });

        return stats;
    } catch (error) {
        throw error;
    }
};

/**
 * Get vehicle history by plate number
 */
export const getVehicleHistory = async (plateNumber, tenantId) => {
    try {
        const upperPlate = plateNumber.toUpperCase();

        const q = query(
            collection(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS}`),
            where('plateNumber', '==', upperPlate)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            startTime: doc.data().startTime?.toDate?.() || doc.data().startTime,
            endTime: doc.data().endTime?.toDate?.() || doc.data().endTime
        }));
    } catch (error) {
        throw error;
    }
};

/**
 * Add note to reservation
 */
export const addNote = async (reservationId, tenantId, note, performedBy) => {
    try {
        const log = await getVehicleMovementLogByReservation(reservationId, tenantId);

        if (log) {
            await logVehicleMovement(log.id, {
                eventType: 'NOTE_ADDED',
                performedBy,
                metadata: { note }
            }, tenantId);
        }

        return { success: true };
    } catch (error) {
        throw error;
    }
};

/**
 * Find reservation by QR code
 * QR code format: RESV-{reservationId} or plate number
 * Prepares structure for future driver QR code integration
 */
export const findReservationByQRCode = async (tenantId, qrCode) => {
    try {
        const cleanCode = qrCode.trim().toUpperCase();

        // Check if it's a reservation ID format
        if (cleanCode.startsWith('RESV-')) {
            const reservationId = cleanCode.replace('RESV-', '');
            const today = new Date().toISOString().split('T')[0];
            const reservations = await getReservationsByDateRange(tenantId, today, today);
            return reservations.find(r => r.id === reservationId) || null;
        }

        // Check if it's a driver QR code format (for future use)
        if (cleanCode.startsWith('DRV-')) {
            // Future: Find reservation by driver code
            // For now, return null
            return null;
        }

        // Otherwise, treat as plate number search
        const today = new Date().toISOString().split('T')[0];
        const reservations = await getReservationsByDateRange(tenantId, today, today);
        return reservations.find(r =>
            (r.plateNumber || '').toUpperCase() === cleanCode
        ) || null;
    } catch (error) {
        throw error;
    }
};

/**
 * Generate QR code data for a reservation
 * This will be used by driver app in the future
 */
export const generateReservationQRData = (reservationId, plateNumber) => {
    return {
        code: `RESV-${reservationId}`,
        plate: plateNumber,
        timestamp: new Date().toISOString()
    };
};

/**
 * Get vehicles currently in facility
 */
export const getVehiclesInFacility = async (tenantId) => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const reservations = await getReservationsByDateRange(tenantId, today, today);

        return reservations.filter(r =>
            [VEHICLE_STATUS.FACILITY_ENTERED, VEHICLE_STATUS.LOADING_STARTED, VEHICLE_STATUS.LOADING_COMPLETED].includes(r.status)
        );
    } catch (error) {
        throw error;
    }
};

/**
 * Get vehicles in parking area
 */
export const getVehiclesInParking = async (tenantId) => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const reservations = await getReservationsByDateRange(tenantId, today, today);

        return reservations.filter(r =>
            r.status === VEHICLE_STATUS.IN_PARKING
        );
    } catch (error) {
        throw error;
    }
};

/**
 * Update vehicle details without changing status
 */
export const updateVehicleDetails = async (reservationId, details, performedBy, tenantId) => {
    try {
        await updateReservation(reservationId, {
            ...details
        }, performedBy, tenantId);

        // Sync driver info
        if (details.driverIdentityId) {
            // We need transporterId, often it's in details or we might need to fetch the reservation first.
            // For now assume it's passed in details or handled by the component.
            await syncDriverInfo({
                transporterId: details.transporterId || null,
                driverName: details.driverName,
                driverPhone: details.driverPhone,
                driverIdentityId: details.driverIdentityId
            }, tenantId);
        }

        // Optionally log this edit
        const log = await getVehicleMovementLogByReservation(reservationId, tenantId);
        if (log) {
            await logVehicleMovement(log.id, {
                eventType: 'NOTE_ADDED',
                performedBy,
                metadata: {
                    note: 'Araç bilgileri güncellendi',
                    updates: details
                }
            }, tenantId);
        }

        return { success: true };
    } catch (error) {
        throw error;
    }
};
